import { useMemo } from 'react';
import { type Task, TaskStatus } from 'src/api/generated';
import { startOfDay, addDays, endOfWeek, isBefore, isValid } from 'date-fns';

export interface DateGroup {
  key: string;
  label: string;
  tasks: Task[];
  isOverdue?: boolean;
}

export interface StatusGroup {
  status: string;
  label: string;
  tasks: Task[];
}

export interface TaskGroups {
  dateGroups: DateGroup[];
  statusGroups: StatusGroup[];
}

/**
 * Hook to group tasks by date and status for task views
 */
export const useTaskGrouping = (
  tasks: Task[]
): { groupedTasks: TaskGroups } => {
  return useMemo(() => {
    const toTimestamp = (value?: string) =>
      value ? new Date(value).getTime() : 0;

    const now = new Date();
    const today = startOfDay(now);
    const tomorrow = addDays(today, 1);
    const dayAfterTomorrow = addDays(today, 2);
    const startOfNextWeek = addDays(startOfDay(endOfWeek(now)), 1);

    const activeTasks = tasks.filter(
      (task) =>
        task.status !== TaskStatus.COMPLETED &&
        task.status !== TaskStatus.CANCELLED
    );

    const finishedTasks = tasks.filter(
      (task) =>
        task.status === TaskStatus.COMPLETED ||
        task.status === TaskStatus.CANCELLED
    );

    const overdueTasks: Task[] = [];
    const todayTasks: Task[] = [];
    const tomorrowTasks: Task[] = [];
    const thisWeekTasks: Task[] = [];
    const laterTasks: Task[] = [];
    const noDateTasks: Task[] = [];

    activeTasks.forEach((task) => {
      if (!task.dueDate) {
        noDateTasks.push(task);
        return;
      }

      const dueDate = new Date(task.dueDate);

      if (!isValid(dueDate)) {
        noDateTasks.push(task);
        return;
      }

      if (isBefore(dueDate, today)) {
        overdueTasks.push(task);
      } else if (isBefore(dueDate, tomorrow)) {
        todayTasks.push(task);
      } else if (isBefore(dueDate, dayAfterTomorrow)) {
        tomorrowTasks.push(task);
      } else if (isBefore(dueDate, startOfNextWeek)) {
        thisWeekTasks.push(task);
      } else {
        laterTasks.push(task);
      }
    });

    const dateGroups: DateGroup[] = [
      {
        key: 'overdue',
        // TODO: check when implementing i18n
        label: 'Vencidas',
        tasks: overdueTasks,
        isOverdue: true,
      },
      {
        key: 'today',
        // TODO: check when implementing i18n
        label: 'Hoy',
        tasks: todayTasks,
      },
      {
        key: 'tomorrow',
        // TODO: check when implementing i18n
        label: 'Mañana',
        tasks: tomorrowTasks,
      },
      {
        key: 'this-week',
        // TODO: check when implementing i18n
        label: 'Esta semana',
        tasks: thisWeekTasks,
      },
      {
        key: 'later',
        // TODO: check when implementing i18n
        label: 'Más adelante',
        tasks: laterTasks,
      },
      {
        key: 'no-date',
        // TODO: check when implementing i18n
        label: 'Sin fecha',
        tasks: noDateTasks,
      },
    ].filter((group) => group.tasks.length > 0);

    const statusGroups: StatusGroup[] = [
      {
        status: 'finished',
        // TODO: check when implementing i18n
        label: 'Finalizadas',
        tasks: finishedTasks.sort((a, b) => {
          const aDate =
            a.status === TaskStatus.COMPLETED
              ? toTimestamp(a.completedAt ?? a.createdAt)
              : toTimestamp(a.cancelledAt ?? a.createdAt);

          const bDate =
            b.status === TaskStatus.COMPLETED
              ? toTimestamp(b.completedAt ?? b.createdAt)
              : toTimestamp(b.cancelledAt ?? b.createdAt);

          return bDate - aDate;
        }),
      },
    ].filter((group) => group.tasks.length > 0);

    return {
      groupedTasks: {
        dateGroups,
        statusGroups,
      },
    };
  }, [tasks]);
};
